/// FOURJS_START_COPYRIGHT(D,2026)
/// Property of Four Js*
/// (c) Copyright Four Js 2026, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("FloatingBannerWidget", ["WidgetBase", "WidgetFactory"], function (context, cls) {
  /**
   * Generic floating banner widget
   * It displays an icon and a content area inside a small panel that can be dragged around
   * @class FloatingBannerWidget
   * @memberOf classes
   * @extends classes.WidgetBase
   */
  cls.FloatingBannerWidget = context.oo.Class(cls.WidgetBase, function ($super) {
    return /** @lends classes.FloatingBannerWidget.prototype */ {
      __name: "FloatingBannerWidget",

      /**
       * Floating panel element that is moved
       * @type {HTMLElement|null}
       */
      _panelElement: null,

      /**
       * Drag handle element used to start moving the panel
       * @type {HTMLElement|null}
       */
      _dragElement: null,

      /**
       * Icon container element
       * @type {HTMLElement|null}
       */
      _iconElement: null,

      /**
       * Header container element
       * @type {HTMLElement|null}
       */
      _headerContentElement: null,

      /**
       * Content container element
       * @type {HTMLElement|null}
       */
      _contentElement: null,

      /**
       * Body container element (collapsible part on mobile)
       * @type {HTMLElement|null}
       */
      _bodyElement: null,

      /**
       * True once the panel has been converted to a "moved" state (fixed positioning with explicit coordinates)
       * @type {boolean}
       */
      _hasBeenMoved: false,

      /**
       * True while an active drag gesture is in progress
       * @type {boolean}
       */
      _isMoving: false,

      /**
       * internal computing information
       * @type {?{current:{x:number, y:number}, reference:{x:number, y:number}, minDelta:{x:number, y:number}, maxDelta:{x:number, y:number}, drag:{x:number, y:number}}}
       */
      _movingPositions: null,

      /**
       * Bound pointermove handler reference
       * @type {Function|null}
       */
      _onMovePointerMove: null,

      /**
       * Bound pointerup handler reference
       * @type {Function|null}
       */
      _onMovePointerUp: null,

      /**
       * Bound pointerdown handler reference
       * @type {Function|null}
       */
      _onPointerDown: null,

      /**
       * Image widget used to render the icon
       * @type {classes.WidgetBase|null}
       */
      _iconWidget: null,

      /**
       * True if floatingBanner has backdrop
       * @type {boolean}
       */
      _hasBackdrop: false,

      /**
       * True if mobile swipe should be enabled
       * @type {boolean}
       */
      _isSwipeEnabled: true,

      /**
       * Handle element used for mobile swipe
       * @type {HTMLElement|null}
       */
      _handleElement: null,

      /**
       * True if bottom sheet (mobile version) is collapsed
       * @type {boolean}
       */
      _isCollapsed: false,

      /**
       * True while swipe gesture is active
       * @type {boolean}
       */
      _isSwiping: false,

      /**
       * Swipe gesture positions
       * @type {{startY:number,currentY:number}|null}
       */
      _swipePositions: null,

      /**
       * Body height captured at swipe start
       * @type {number}
       */
      _swipeStartBodyHeight: 0,

      /**
       * Bound swipe handlers
       */
      _onSwipePointerDown: null,
      _onSwipePointerMove: null,
      _onSwipePointerUp: null,

      /**
       * Indicates whether this widget is used for AI-related content
       * @type {boolean}
       */
      _isAIWidget: false,

      /**
       * @inheritDoc
       */
      constructor: function (opts) {
        this._isSystemWidget = true;
        $super.constructor.call(this, opts);
      },

      /**
       * @inheritDoc
       */
      _initElement: function () {
        $super._initElement.call(this);

        this._panelElement = this._element.getElementsByClassName("gbc_FloatingBannerWidget__panel")[0];
        this._dragElement = this._element.getElementsByClassName("gbc_FloatingBannerWidget__drag")[0];
        this._iconElement = this._element.getElementsByClassName("gbc_FloatingBannerWidget__icon")[0];
        this._headerContentElement = this._element.getElementsByClassName("gbc_FloatingBannerWidget__headerContent")[0];
        this._contentElement = this._element.getElementsByClassName("gbc_FloatingBannerWidget__content")[0];
        this._bodyElement = this._element.getElementsByClassName("gbc_FloatingBannerWidget__body")[0];
        this._handleElement = this._element.getElementsByClassName("gbc_FloatingBannerWidget__handle")[0];

        this._initMovable();
        this._initSwipe();
        this.setSwipeEnabled(this._isSwipeEnabled);
        this.hide();
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        if (this._onMovePointerMove) {
          document.removeEventListener("pointermove", this._onMovePointerMove);
          this._onMovePointerMove = null;
        }
        if (this._onMovePointerUp) {
          document.removeEventListener("pointerup", this._onMovePointerUp);
          this._onMovePointerUp = null;
        }
        this._isMoving = false;

        if (this._dragElement && this._onPointerDown) {
          this._dragElement.removeEventListener("pointerdown", this._onPointerDown);
          this._onPointerDown = null;
        }

        if (this._iconWidget) {
          this._iconWidget.destroy();
          this._iconWidget = null;
        }

        if (this._onSwipePointerMove) {
          document.removeEventListener("pointermove", this._onSwipePointerMove);
          this._onSwipePointerMove = null;
        }
        if (this._onSwipePointerUp) {
          document.removeEventListener("pointerup", this._onSwipePointerUp);
          this._onSwipePointerUp = null;
        }
        if (this._handleElement && this._onSwipePointerDown) {
          this._handleElement.removeEventListener("pointerdown", this._onSwipePointerDown);
          this._onSwipePointerDown = null;
        }

        if (this._panelElement) {
          this._panelElement.classList.remove("gbc_swiping");
        }
        if (this._bodyElement) {
          this._bodyElement.style.removeProperty("max-height");
          this._bodyElement.style.removeProperty("opacity");
        }

        this._swipeStartBodyHeight = 0;
        this._headerContentElement = null;
        this._isSwiping = false;
        this._swipePositions = null;
        this._handleElement = null;
        this._bodyElement = null;
        this._panelElement = null;
        this._dragElement = null;
        this._iconElement = null;
        this._contentElement = null;
        this._movingPositions = null;

        $super.destroy.call(this);
      },

      /**
       * Returns whether this widget is AI-related
       * @returns {boolean}
       */
      isAIWidget: function () {
        return this._isAIWidget;
      },

      /**
       * Sets whether this widget is AI-related
       * @param {boolean} isAIWidget
       */
      setIsAIWidget: function (isAIWidget) {
        this._isAIWidget = isAIWidget;

        if (this._element) {
          this._element.classList.toggle("gbc_aiWidget", this._isAIWidget);
        }
      },

      /**
       * Start the movable process
       * @private
       */
      _initMovable: function () {
        this._resetMovable();
        if (cls.WindowHelper.isMobile()) {
          return;
        }
        this._onPointerDown = this._onMovablePointerDown.bind(this);
        this._dragElement.addEventListener("pointerdown", this._onPointerDown);
      },

      /**
       * Reset all values used by the drag-to-move feature
       * @private
       */
      _resetMovable: function () {
        this._movingPositions = {
          reference: { x: 0, y: 0 },
          current: { x: 0, y: 0 },
          minDelta: { x: 0, y: 0 },
          maxDelta: { x: 0, y: 0 },
          drag: { x: 0, y: 0 }
        };
      },

      /**
       * Move at given position
       * @param {number} x x position
       * @param {number} y y position
       * @private
       */
      _setAsMoved: function (x, y) {
        this._panelElement.classList.add("gbc_moved");
        this._panelElement.style.left = x + "px";
        this._panelElement.style.top = y + "px";
        this._hasBeenMoved = true;

        this._movingPositions.reference.x = x;
        this._movingPositions.reference.y = y;
      },

      /**
       * Handle on pointer down for move
       * @param {PointerEvent} evt the event
       * @private
       */
      _onMovablePointerDown: function (evt) {
        evt.preventDefault();

        const panelRect = this._panelElement.getBoundingClientRect();

        if (!this._hasBeenMoved) {
          this._setAsMoved(panelRect.left, panelRect.top);
        }

        this._panelElement.classList.add("gbc_moving");

        const dragRect = this._dragElement.getBoundingClientRect();
        const dragOffsetX = dragRect.left - panelRect.left;
        const dragOffsetY = dragRect.top - panelRect.top;

        const minPanelLeft = dragRect.width - dragOffsetX - dragRect.width;
        const maxPanelLeft = window.innerWidth - dragRect.width - dragOffsetX;

        const minPanelTop = dragRect.height - dragOffsetY - dragRect.height;
        const maxPanelTop = window.innerHeight - dragRect.height - dragOffsetY;

        this._movingPositions.minDelta.x = minPanelLeft - this._movingPositions.reference.x;
        this._movingPositions.maxDelta.x = maxPanelLeft - this._movingPositions.reference.x;

        this._movingPositions.minDelta.y = minPanelTop - this._movingPositions.reference.y;
        this._movingPositions.maxDelta.y = maxPanelTop - this._movingPositions.reference.y;

        this._isMoving = true;
        this._movingPositions.current.x = evt.clientX;
        this._movingPositions.current.y = evt.clientY;
        this._movingPositions.drag.x = evt.clientX;
        this._movingPositions.drag.y = evt.clientY;

        this._onMovePointerMove = this._onMovablePointerMove.bind(this);
        this._onMovePointerUp = this._onMovablePointerUp.bind(this);

        document.addEventListener("pointermove", this._onMovePointerMove, { passive: false });
        document.addEventListener("pointerup", this._onMovePointerUp, { passive: false });
      },

      /**
       * Handle on pointer move for move
       * @param {PointerEvent} evt the event
       * @private
       */
      _onMovablePointerMove: function (evt) {
        if (!this._isMoving) {
          return;
        }

        evt.preventDefault();

        this._movingPositions.current.x = evt.clientX;
        this._movingPositions.current.y = evt.clientY;

        const deltaX = this._movingPositions.current.x - this._movingPositions.drag.x;
        const deltaY = this._movingPositions.current.y - this._movingPositions.drag.y;

        const clampedX = Math.min(Math.max(deltaX, this._movingPositions.minDelta.x), this._movingPositions.maxDelta.x);
        const clampedY = Math.min(Math.max(deltaY, this._movingPositions.minDelta.y), this._movingPositions.maxDelta.y);

        this._updateMovablePosition(clampedX, clampedY);
      },

      /**
       * Update visual position when moving
       * @param {number} deltaX x position delta
       * @param {number} deltaY y position delta
       * @private
       */
      _updateMovablePosition: function (deltaX, deltaY) {
        this._panelElement.style.left = this._movingPositions.reference.x + deltaX + "px";
        this._panelElement.style.top = this._movingPositions.reference.y + deltaY + "px";
      },

      /**
       * Handle on pointer up for move
       * @param {PointerEvent} evt the event
       * @private
       */
      _onMovablePointerUp: function () {
        if (!this._isMoving) {
          return;
        }
        this._panelElement.classList.remove("gbc_moving");

        const deltaX = this._movingPositions.current.x - this._movingPositions.drag.x;
        const deltaY = this._movingPositions.current.y - this._movingPositions.drag.y;

        const clampedX = Math.min(Math.max(deltaX, this._movingPositions.minDelta.x), this._movingPositions.maxDelta.x);
        const clampedY = Math.min(Math.max(deltaY, this._movingPositions.minDelta.y), this._movingPositions.maxDelta.y);

        this._movingPositions.reference.x = this._movingPositions.reference.x + clampedX;
        this._movingPositions.reference.y = this._movingPositions.reference.y + clampedY;

        this._isMoving = false;

        document.removeEventListener("pointermove", this._onMovePointerMove);
        document.removeEventListener("pointerup", this._onMovePointerUp);
      },

      /**
       * Initialize mobile swipe handling on the bottom-sheet handle
       * @private
       */
      _initSwipe: function () {
        if (!this._handleElement) {
          return;
        }

        this._swipePositions = { startY: 0, currentY: 0 };
        this._onSwipePointerDown = this._onBottomSheetPointerDown.bind(this);
        this._handleElement.addEventListener("pointerdown", this._onSwipePointerDown);
      },

      /**
       * Enable or disable mobile swipe mode (and its visual handle)
       * @param {boolean} enable
       */
      setSwipeEnabled: function (enable = true) {
        if (!this._element) {
          return;
        }
        this._isSwipeEnabled = enable;
        this._element.classList.toggle("gbc_swipeDisabled", !this._isSwipeEnabled);
        if (!this._isSwipeEnabled) {
          this.setCollapsed(false);
        }
      },

      /**
       * Returns whether swipe is enabled
       * @returns {boolean}
       */
      isSwipeEnabled: function () {
        return this._isSwipeEnabled;
      },

      /**
       * Set the collapsed state of the mobile bottom-sheet handle
       * @param {boolean} [collapsed=false] - True to collapse, false to expand
       */
      setCollapsed: function (collapsed = false) {
        if (!this._element) {
          return;
        }
        this._isCollapsed = collapsed;
        this._element.classList.toggle("gbc_collapsed", this._isCollapsed);
      },

      /**
       * Returns whether the mobile bottom sheet is collapsed
       * @returns {boolean}
       */
      isCollapsed: function () {
        return this._isCollapsed;
      },

      /**
       * Start swipe gesture on bottom-sheet handle
       * @param {PointerEvent} evt
       * @private
       */
      _onBottomSheetPointerDown: function (evt) {
        if (!cls.WindowHelper.isMobile() || !this._isSwipeEnabled) {
          return;
        }

        evt.preventDefault();

        this._isSwiping = true;
        this._swipePositions.startY = evt.clientY;
        this._swipePositions.currentY = evt.clientY;

        if (this._bodyElement) {
          this._swipeStartBodyHeight = this._bodyElement.getBoundingClientRect().height;
        } else {
          this._swipeStartBodyHeight = 0;
        }
        this._panelElement.classList.add("gbc_swiping");

        if (this._bodyElement) {
          this._bodyElement.style.removeProperty("max-height");
          this._bodyElement.style.removeProperty("opacity");
        }

        this._onSwipePointerMove = this._onBottomSheetPointerMove.bind(this);
        this._onSwipePointerUp = this._onBottomSheetPointerUp.bind(this);

        document.addEventListener("pointermove", this._onSwipePointerMove, { passive: false });
        document.addEventListener("pointerup", this._onSwipePointerUp, { passive: false });
      },

      /**
       * Update swipe gesture and preview collasped/expanded body height
       * @param {PointerEvent} evt
       * @private
       */
      _onBottomSheetPointerMove: function (evt) {
        if (!this._isSwiping) {
          return;
        }

        evt.preventDefault();

        this._swipePositions.currentY = evt.clientY;
        const deltaY = this._swipePositions.currentY - this._swipePositions.startY;

        if (this._isCollapsed) {
          // collapsed: swipe up => reveal body
          if (deltaY < 0) {
            const reveal = Math.min(-deltaY, 320);
            this._bodyElement.style.maxHeight = reveal + "px";
            this._bodyElement.style.opacity = "1";
          } else {
            this._bodyElement.style.removeProperty("max-height");
            this._bodyElement.style.removeProperty("opacity");
          }
        } else if (deltaY > 0) {
          // expanded: swipe down => reduce visible body
          const nextHeight = Math.max(0, this._swipeStartBodyHeight - deltaY);
          this._bodyElement.style.maxHeight = nextHeight + "px";
          this._bodyElement.style.opacity = String(Math.max(0.35, nextHeight / Math.max(this._swipeStartBodyHeight, 1)));
        } else {
          this._bodyElement.style.removeProperty("max-height");
          this._bodyElement.style.removeProperty("opacity");
        }
      },

      /**
       * End swipe gesture and apply collapsed/expanded state depending on swipe direction
       * @private
       */
      _onBottomSheetPointerUp: function () {
        if (!this._isSwiping) {
          return;
        }

        const deltaY = this._swipePositions.currentY - this._swipePositions.startY;
        const threshold = 24;

        this._isSwiping = false;

        document.removeEventListener("pointermove", this._onSwipePointerMove);
        document.removeEventListener("pointerup", this._onSwipePointerUp);
        this._onSwipePointerMove = null;
        this._onSwipePointerUp = null;

        if (this._bodyElement) {
          this._bodyElement.style.removeProperty("max-height");
          this._bodyElement.style.removeProperty("opacity");
        }

        if (!this._isCollapsed && deltaY > threshold) {
          this.setCollapsed(true);
        } else if (this._isCollapsed && deltaY < -threshold) {
          this.setCollapsed(false);
        }

        this._panelElement.classList.remove("gbc_swiping");
      },

      /**
       * Display the banner widget
       * Removes the hidden state and updates ARIA attributes
       */
      show: function () {
        if (!this._element) {
          return;
        }

        if (cls.WindowHelper.isMobile()) {
          this.setCollapsed(false);
        }

        this._hidden = false;

        this._element.style.display = "";
        this.setAriaAttribute("hidden", "false");

        this._activateSystemWidget();
      },

      /**
       * Hide the banner widget
       * Does nothing if the widget is already hidden
       * Updates ARIA attributes accordingly
       */
      hide: function () {
        if (!this._element || this.isHidden()) {
          return;
        }

        const active = document.activeElement;
        if (active instanceof HTMLElement && this._element.contains(active)) {
          active.blur();
        }

        this._hidden = true;

        this._element.style.display = "none";
        this.setAriaAttribute("hidden", "true");

        this._deactivateSystemWidget();
      },

      /**
       * Enable or disable the banner backdrop
       * @param {boolean} enable
       * @param {boolean} [transparent=false]
       */
      setBackdrop: function (enable, transparent = false) {
        if (!this._element) {
          return;
        }
        this._hasBackdrop = enable;
        this._element.classList.toggle("gbc_hasBackdrop", enable);
        this._element.classList.toggle("gbc_hasTransparentBackdrop", enable && transparent);
      },

      /**
       * Set (or clear) the banner icon
       * If an icon is provided, it is rendered through a GbcImage widget
       * Passing a falsy value removes the icon and destroys the inner widget
       * @param {string|null} icon - Icon identifier / zmdi class name (e.g. "zmdi-wifi-off")
       */
      setIcon: function (icon) {
        if (!this._iconElement) {
          return;
        }

        if (!icon) {
          if (this._iconWidget) {
            this._iconWidget.destroy();
            this._iconWidget = null;
          }
          this._iconElement.replaceChildren();
          return;
        }

        if (!this._iconWidget) {
          this._iconWidget = cls.WidgetFactory.createWidget("Image", this.getBuildParameters());
          this._iconWidget.setParentWidget(this);
          this._iconWidget.setAutoScale(true);
        }

        this._iconWidget.setSrc(icon);

        if (this._iconElement.firstChild !== this._iconWidget.getElement()) {
          this._iconElement.replaceChildren(this._iconWidget.getElement());
        }
      },

      /**
       * Set the CSS color applied to the icon
       * @param {string} color - Any valid CSS color
       */
      setIconColor: function (color) {
        if (!this._iconElement || !color) {
          return;
        }

        this._iconElement.style.color = color;
      },

      /**
       * Set the CSS z-index on the banner root element
       * @param {number|string} zindex - Any valid CSS z-index value
       */
      setZIndex: function (zindex) {
        if (!this._element) {
          return;
        }

        this._element.style.zIndex = zindex;
      },

      /**
       * Replace the header banner content
       * @param {Node|null} content - DOM node (e.g. HTMLElement/Text/DocumentFragment) to append. If null, clears the content
       */
      setHeaderContent: function (content = null) {
        if (!this._headerContentElement) {
          return;
        }
        this._headerContentElement.replaceChildren();
        if (content !== null) {
          this._headerContentElement.append(content);
        }
      },

      /**
       * Replace the banner content
       * @param {Node|null} content - DOM node (e.g. HTMLElement/Text/DocumentFragment) to append. If null, clears the content
       */
      setContent: function (content = null) {
        if (!this._contentElement) {
          return;
        }
        this._contentElement.replaceChildren();
        if (content !== null) {
          this._contentElement.append(content);
        }
      },

      /**
       * Position the floating banner relative to a target element
       * Existing manual move state is reset before applying the new position
       * @param {HTMLElement} element - Reference element used as anchor
       * @param {"top"|"right"|"bottom"|"left"} [placement="bottom"] - Side where the banner is placed
       * @param {number} [offsetX=0] - Horizontal offset in pixels
       * @param {number} [offsetY=8] - Vertical offset in pixels
       */
      setPositionFromElement: function (element, placement = "bottom", offsetX = 0, offsetY = 8) {
        if (!this._panelElement || cls.WindowHelper.isMobile()) {
          return;
        }

        if (this._hasBeenMoved) {
          this._panelElement.classList.remove("gbc_moved");
          this._panelElement.style.left = "";
          this._panelElement.style.top = "";
          this._hasBeenMoved = false;
          this._resetMovable();
        }

        const wasHidden = this.isHidden();
        if (wasHidden) {
          this._element.style.visibility = "hidden";
          this.show();
        }

        const elementRect = element.getBoundingClientRect();
        const panelRect = this._panelElement.getBoundingClientRect();

        let left = 0;
        let top = 0;

        switch (placement) {
          case "top":
            left = elementRect.left + (elementRect.width - panelRect.width) / 2 + offsetX;
            top = elementRect.top - panelRect.height - offsetY;
            break;

          case "left":
            left = elementRect.left - panelRect.width - offsetX;
            top = elementRect.top + (elementRect.height - panelRect.height) / 2 + offsetY;
            break;

          case "right":
            left = elementRect.right + offsetX;
            top = elementRect.top + (elementRect.height - panelRect.height) / 2 + offsetY;
            break;

          case "bottom":
          default:
            left = elementRect.left + (elementRect.width - panelRect.width) / 2 + offsetX;
            top = elementRect.bottom + offsetY;
            break;
        }

        // Keep panel inside viewport horizontally
        const viewportPadding = Number.parseFloat(context.ThemeService.getValue("gbc-space-s"));
        const minLeft = viewportPadding;
        const maxLeft = Math.max(minLeft, window.innerWidth - panelRect.width - viewportPadding);
        left = Math.min(Math.max(left, minLeft), maxLeft);

        this._setAsMoved(left, top);
        if (wasHidden) {
          this.hide();
          this._element.style.visibility = "";
        }
      }
    };
  });
  cls.WidgetFactory.registerBuilder("FloatingBanner", cls.FloatingBannerWidget);
});
